import { Box, Hide, Show, Text } from '@chakra-ui/react';
import { EmbedVideoWithOverlay, Grid } from '@time-line/components/common';
import type { SanityImage } from '@time-line/components/types';
import { PageModuleResolver } from 'components/common/PageModuleResolver';
import { PageWrapper } from 'components/common/PageWrapper';
import {
  AboutPageMissionContainer,
  RichTextResolver,
} from 'components/common/RichTextResolver';
import { Seo } from 'components/common/Seo';
import { useAbout } from 'hooks/pages/useAbout';
import { useAnalytics } from 'hooks/useAnalytics';
import { useCallback } from 'react';
import type { ResponsiveGif } from 'types/cms/pages/AboutPage';

const DESKTOP_RATIO = '16:7';
const MOBILE_RATIO = '2:3';

export function About() {
  const { data: about } = useAbout();
  const { analytics } = useAnalytics();

  const getMediaProps = (isMobile?: boolean) =>
    about.hero.heroMedia[0]._type === 'gif'
      ? {
          overlayGif: isMobile
            ? (about.hero.heroMedia[0] as ResponsiveGif).mobile
            : (about.hero.heroMedia[0] as ResponsiveGif).desktop,
        }
      : {
          overlayImage: about.hero.heroMedia[0] as SanityImage,
          imageSizes: '90vw',
        };

  const handleVideoPlay = useCallback(() => {
    analytics.aboutVideoPlay();
  }, [analytics]);

  return (
    <PageWrapper as="main">
      <Seo seo={about.seo} />
      <Grid pt={{ base: '2xl', md: '3xl' }} mb="3xl">
        <Grid.Inner pb="sm" gridRowGap={{ base: 'm', lg: 'xl' }}>
          <Box gridColumn={{ base: '1 / -1', md: '3 / span 8' }}>
            <Text
              as="h1"
              size="h1"
              textAlign="center"
              sx={{ textWrap: 'balance' }}
            >
              {about.hero.title}
            </Text>
          </Box>
          <Box gridColumn="span 12">
            <Hide below="sm">
              <EmbedVideoWithOverlay
                videoUrl={about.hero.heroVideoUrl}
                ratio={DESKTOP_RATIO}
                onPlay={handleVideoPlay}
                {...getMediaProps()}
              />
            </Hide>
            <Show below="sm">
              <EmbedVideoWithOverlay
                videoUrl={about.hero.heroVideoUrl}
                ratio={MOBILE_RATIO}
                onPlay={handleVideoPlay}
                {...getMediaProps(true)}
              />
            </Show>
          </Box>
        </Grid.Inner>
        <Grid.Inner pt="xl" gridRowGap="2xs">
          <Text gridColumn="span 12" as="h2" size="overline" textAlign="center">
            {about.mission.title}
          </Text>
          <Box gridColumn={{ base: '1 / -1', lg: '3 / span 8' }}>
            <RichTextResolver
              blocks={about.mission.description}
              container={AboutPageMissionContainer}
            />
          </Box>
        </Grid.Inner>
      </Grid>
      {about?.sections && (
        <PageModuleResolver blocks={about.sections} pageTitle="About us" />
      )}
    </PageWrapper>
  );
}
